import React from 'react';

import Chip from '@components/Chip';
import { useCurrentUser } from '@context/UsersContext';
import { usePlanLikeMutation, usePlanLikeStatusQuery } from '@db/Plans/Queries';
import { errorAlert } from '@helpers/Alert';
import { formatNumber } from '@helpers/Formatters';
import { getCurrentUserId } from '@helpers/firebase/Auth';
import { Plan } from '@types';

export type PlanLikeButtonProps = {
  planDoc: Pick<Plan, 'planId' | 'numLikes'>;
  testID?: string;
};

export default function PlanLikeButton({ planDoc, testID }: PlanLikeButtonProps) {
  const { userDoc } = useCurrentUser();
  const currentUserId = getCurrentUserId();
  const baseNumLikes = planDoc.numLikes || 0;
  const [optimisticLikes, setOptimisticLikes] = React.useState<
    { baseNumLikes: number; numLikes: number } | undefined
  >();
  const numLikes =
    optimisticLikes?.baseNumLikes === baseNumLikes ? optimisticLikes.numLikes : baseNumLikes;
  const likeStatusQuery = usePlanLikeStatusQuery({
    planId: planDoc.planId,
    userId: currentUserId,
  });
  const likeMutation = usePlanLikeMutation({
    currentUser: userDoc,
    planId: planDoc.planId,
    userId: currentUserId,
  });

  const onLikePress = () => {
    const previousNumLikes = numLikes;
    const nextUserLikes = !likeStatusQuery.userLikes;
    setOptimisticLikes({
      baseNumLikes,
      numLikes: Math.max(0, numLikes + (nextUserLikes ? 1 : -1)),
    });
    likeMutation.mutate(
      { userLikes: nextUserLikes },
      {
        onError: (error) => {
          setOptimisticLikes({ baseNumLikes, numLikes: previousNumLikes });
          errorAlert(error, { onAction: onLikePress });
        },
      }
    );
  };

  return (
    <Chip
      onPress={onLikePress}
      icon={likeStatusQuery.userLikes ? 'heart' : 'heart-outline'}
      disabled={likeStatusQuery.loading || likeMutation.isPending}
      testID={testID}>
      {numLikes ? `${formatNumber(numLikes)} Like` + (numLikes > 1 ? 's' : '') : 'Like'}
    </Chip>
  );
}
